"use client";
import React, { useState, useEffect, useRef } from "react";
import { Button, Card, CardBody, Col, Container, Row } from "reactstrap";
import { toast } from "react-toastify";
import Api from "../../api/Api";

//Import Breadcrumb
import Breadcrumbs from "../../components/Common/Breadcrumb";

const ContentPages = () => {
  const [modal_center, setmodal_center] = useState(false);
  const [selectedPage, setSelectedPage] = useState({});
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [quillLoaded, setQuillLoaded] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    content: "",
    slug: "",
  });
  const quillRef = useRef(null);
  const quillInstance = useRef(null);

  document.title = "Content Pages | Tafaseel Admin";

  // Load Quill.js from CDN
  useEffect(() => {
    const loadQuill = () => {
      // Load Quill CSS
      const quillCSS = document.createElement("link");
      quillCSS.rel = "stylesheet";
      quillCSS.href = "https://cdn.quilljs.com/1.3.6/quill.snow.css";
      document.head.appendChild(quillCSS);

      // Load Quill JS
      const quillScript = document.createElement("script");
      quillScript.src = "https://cdn.quilljs.com/1.3.6/quill.min.js";
      quillScript.onload = () => {
        setQuillLoaded(true);
      };
      quillScript.onerror = () => {
        toast.error("Failed to load editor. Please refresh the page.", {
          position: "bottom-center",
          autoClose: 3000,
          theme: "colored",
        });
      };
      document.head.appendChild(quillScript);
    };

    if (!window.Quill) {
      loadQuill();
    } else {
      setQuillLoaded(true);
    }
  }, []);

  // Fetch pages from API
  const fetchPages = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await Api.get("/admin/content-pages", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.success) {
        setPages(response.data.data);
      } else {
        toast.error(response.data.message || "Failed to fetch pages", {
          position: "bottom-center",
          autoClose: 3000,
          theme: "colored",
        });
      }
    } catch (error) {
      console.error("Error fetching pages:", error);
      toast.error("Network error. Please check your connection.", {
        position: "bottom-center",
        autoClose: 3000,
        theme: "colored",
      });
    } finally {
      setLoading(false);
    }
  };

  // Load pages on component mount
  useEffect(() => {
    fetchPages();
  }, []);

  // Initialize Quill editor
  const initializeQuill = (initialContent = "") => {
    if (!quillLoaded || !window.Quill || !quillRef.current) return;

    if (quillInstance.current) {
      quillInstance.current = null;
    }

    try {
      quillInstance.current = new window.Quill(quillRef.current, {
        theme: "snow",
        placeholder: "Start writing your content here...",
        modules: {
          toolbar: [
            [{ header: [1, 2, 3, 4, false] }],
            ["bold", "italic", "underline", "strike"],
            [{ list: "ordered" }, { list: "bullet" }],
            [{ indent: "-1" }, { indent: "+1" }],
            ["blockquote", "code-block"],
            ["link", "image"],
            [{ align: [] }],
            [{ color: [] }, { background: [] }],
            ["clean"],
          ],
        },
        formats: [
          "header",
          "bold",
          "italic",
          "underline",
          "strike",
          "list",
          "bullet",
          "indent",
          "blockquote",
          "code-block",
          "link",
          "image",
          "align",
          "color",
          "background",
        ],
      });

      // Set initial content
      if (initialContent) {
        quillInstance.current.root.innerHTML = initialContent;
      }
    } catch (error) {
      console.error("Error initializing Quill:", error);
      toast.error("Failed to initialize editor. Please refresh the page.", {
        position: "bottom-center",
        autoClose: 3000,
        theme: "colored",
      });
    }
  };

  function tog_center() {
    setmodal_center(!modal_center);
    setSelectedPage({});
    setFormData({ title: "", content: "", slug: "" });

    if (!modal_center) {
      // Opening modal - initialize editor after a short delay
      setTimeout(() => {
        if (quillLoaded) {
          initializeQuill();
        }
      }, 300);
    } else {
      // Closing modal - cleanup
      if (quillInstance.current) {
        quillInstance.current = null;
      }
    }
  }

  const handleEdit = (page) => {
    setSelectedPage(page);
    setFormData({
      title: page.title,
      content: page.content || "",
      slug: page.slug,
    });
    setmodal_center(true);

    // Initialize editor with existing content after modal opens
    setTimeout(() => {
      if (quillLoaded) {
        initializeQuill(page.content || "");
      }
    }, 300);
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      if (!quillInstance.current) {
        toast.error("Editor not initialized. Please try again.", {
          position: "bottom-center",
          autoClose: 3000,
          theme: "colored",
        });
        return;
      }

      // Get HTML content from Quill
      const htmlContent = quillInstance.current.root.innerHTML;

      const token = localStorage.getItem("token");
      const response = await Api.put(
        `/admin/content-pages/${selectedPage.id}`,
        {
          ...formData,
          content: htmlContent,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        // Update the pages list
        setPages(
          pages.map((page) =>
            page.id === selectedPage.id
              ? {
                  ...page,
                  ...formData,
                  content: htmlContent,
                  updated_at: new Date().toISOString(),
                }
              : page
          )
        );
        setmodal_center(false);
        setSelectedPage({});
        setFormData({ title: "", content: "", slug: "" });

        // Cleanup editor
        if (quillInstance.current) {
          quillInstance.current = null;
        }

        toast.success("Page updated successfully!", {
          position: "bottom-center",
          autoClose: 3000,
          theme: "colored",
        });
      } else {
        toast.error(response.data.message || "Failed to update page", {
          position: "bottom-center",
          autoClose: 3000,
          theme: "colored",
        });
      }
    } catch (error) {
      console.error("Error updating page:", error);
      toast.error("Network error. Please try again.", {
        position: "bottom-center",
        autoClose: 3000,
        theme: "colored",
      });
    } finally {
      setSaving(false);
    }
  };

  const stripHtml = (html) => {
    const tmp = document.createElement("DIV");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid={true}>
          <Breadcrumbs title="Tafaseel Admin" breadcrumbItem="Content Pages" />
          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <div id="contentPagesList">
                    <Row className="g-4 mb-3">
                      <Col className="col-sm-auto">
                        <div className="d-flex gap-1">
                          <Button
                            color="primary"
                            className="refresh-btn"
                            onClick={fetchPages}
                            disabled={loading}
                          >
                            <i className="ri-refresh-line align-bottom me-1"></i>
                            {loading ? "Loading..." : "Refresh"}
                          </Button>
                        </div>
                      </Col>
                      <Col className="col-sm">
                        <div className="d-flex justify-content-sm-end">
                          <div className="text-muted">
                            Manage your app's content pages with Quill editor
                            {!quillLoaded && (
                              <span className="ms-2">
                                <i className="ri-loader-4-line spinner-border spinner-border-sm"></i>
                                Loading editor...
                              </span>
                            )}
                          </div>
                        </div>
                      </Col>
                    </Row>

                    <div className="table-responsive table-card mt-3 mb-1">
                      {loading ? (
                        <div className="text-center p-4">
                          <div
                            className="spinner-border text-primary"
                            role="status"
                          >
                            <span className="sr-only">Loading...</span>
                          </div>
                          <p className="mt-2">Loading pages...</p>
                        </div>
                      ) : (
                        <table
                          className="table align-middle table-nowrap"
                          id="contentPagesTable"
                        >
                          <thead className="table-light">
                            <tr>
                              <th className="sort" data-sort="page_title">
                                Page
                              </th>
                              <th className="sort" data-sort="slug">
                                Slug
                              </th>
                              <th className="sort" data-sort="content_preview">
                                Content Preview
                              </th>
                              <th className="sort" data-sort="updated_at">
                                Last Updated
                              </th>
                              <th className="sort" data-sort="action">
                                Action
                              </th>
                            </tr>
                          </thead>
                          <tbody className="list form-check-all">
                            {pages.length > 0 ? (
                              pages.map((page) => (
                                <tr key={page.id}>
                                  <td>
                                    <div className="d-flex align-items-center">
                                      <div className="flex-shrink-0 me-3">
                                        <div className="avatar-sm bg-light rounded-circle d-flex align-items-center justify-content-center">
                                          <i className="ri-file-text-line text-primary fs-16"></i>
                                        </div>
                                      </div>
                                      <div className="flex-grow-1">
                                        <h6 className="fs-14 mb-1">
                                          {page.title}
                                        </h6>
                                        <p className="text-muted mb-0">
                                          {page.slug === "privacy-policy"
                                            ? "Privacy Policy"
                                            : "Terms of Use"}
                                        </p>
                                      </div>
                                    </div>
                                  </td>
                                  <td>
                                    <span className="badge badge-soft-info fs-12">
                                      {page.slug}
                                    </span>
                                  </td>
                                  <td>
                                    <div className="text-muted">
                                      {page.content
                                        ? `${stripHtml(page.content).substring(
                                            0,
                                            80
                                          )}...`
                                        : "No content available"}
                                    </div>
                                  </td>
                                  <td>
                                    <div className="text-muted">
                                      {new Date(
                                        page.updated_at
                                      ).toLocaleDateString("en-US", {
                                        year: "numeric",
                                        month: "short",
                                        day: "numeric",
                                        hour: "2-digit",
                                        minute: "2-digit",
                                      })}
                                    </div>
                                  </td>
                                  <td>
                                    <div className="d-flex gap-2">
                                      <div className="edit">
                                        <button
                                          className="btn btn-sm btn-success edit-item-btn"
                                          onClick={() => handleEdit(page)}
                                          disabled={!quillLoaded}
                                        >
                                          <i className="ri-pencil-fill align-bottom"></i>{" "}
                                          Edit
                                        </button>
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="5" className="text-center p-4">
                                  <div className="text-center">
                                    <lord-icon
                                      src="https://cdn.lordicon.com/msoeawqm.json"
                                      trigger="loop"
                                      colors="primary:#121331,secondary:#08a88a"
                                      style={{ width: "75px", height: "75px" }}
                                    ></lord-icon>
                                    <h5 className="mt-2">No Pages Found</h5>
                                    <p className="text-muted mb-0">
                                      No content pages available at the moment.
                                    </p>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Edit Modal with Quill.js */}
          {modal_center && (
            <div
              className="modal fade show d-block"
              tabIndex="-1"
              role="dialog"
            >
              <div
                className="modal-dialog modal-dialog-centered modal-xl"
                role="document"
              >
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">
                      <i className="ri-edit-box-line me-2"></i>
                      Edit {selectedPage?.title}
                    </h5>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={tog_center}
                      aria-label="Close"
                    ></button>
                  </div>
                  <div className="modal-body">
                    <form>
                      <div className="row">
                        <div className="col-lg-12">
                          <div className="mb-3">
                            <label
                              htmlFor="page-title-input"
                              className="form-label"
                            >
                              Title <span className="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              id="page-title-input"
                              placeholder="Enter page title"
                              value={formData.title}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  title: e.target.value,
                                })
                              }
                              required
                            />
                          </div>
                        </div>
                        <div className="col-lg-12">
                          <div className="mb-3">
                            <label
                              htmlFor="page-slug-input"
                              className="form-label"
                            >
                              Slug
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              id="page-slug-input"
                              value={formData.slug}
                              disabled
                              readOnly
                            />
                            <div className="form-text">
                              The slug cannot be changed as it's used by the
                              mobile app.
                            </div>
                          </div>
                        </div>
                        <div className="col-lg-12">
                          <div className="mb-3">
                            <label className="form-label">
                              Content <span className="text-danger">*</span>
                            </label>
                            <div className="form-text mb-2">
                              <i className="ri-information-line me-1"></i>
                              Use the rich text editor below to create formatted
                              content with headings, lists, links, and more.
                              {!quillLoaded && (
                                <span className="text-warning ms-2">
                                  <i className="ri-loader-4-line spinner-border spinner-border-sm"></i>
                                  Editor is loading...
                                </span>
                              )}
                            </div>

                            {/* Quill.js Container */}
                            <div
                              className="border rounded"
                              style={{
                                minHeight: "400px",
                                opacity: quillLoaded ? 1 : 0.5,
                              }}
                            >
                              {!quillLoaded && (
                                <div className="text-center p-5">
                                  <div
                                    className="spinner-border text-primary"
                                    role="status"
                                  >
                                    <span className="sr-only">Loading...</span>
                                  </div>
                                  <p className="mt-2 text-muted">
                                    Loading Quill editor...
                                  </p>
                                </div>
                              )}
                              <div
                                ref={quillRef}
                                style={{
                                  minHeight: "350px",
                                  display: quillLoaded ? "block" : "none",
                                }}
                              />
                            </div>

                            <div className="form-text mt-2">
                              <strong>Available formatting:</strong> Headers,
                              Bold, Italic, Lists, Links, Images, Blockquotes,
                              Code blocks
                              <br />
                              <strong>Tip:</strong> Use the toolbar above the
                              editor to format your content
                            </div>
                          </div>
                        </div>
                      </div>
                    </form>
                  </div>
                  <div className="modal-footer">
                    <div className="hstack gap-2 justify-content-end">
                      <button
                        type="button"
                        className="btn btn-light"
                        onClick={tog_center}
                      >
                        <i className="ri-close-line me-1"></i>
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="btn btn-success"
                        onClick={handleSave}
                        disabled={saving || !quillLoaded}
                      >
                        {saving ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                              aria-hidden="true"
                            ></span>
                            Saving...
                          </>
                        ) : (
                          <>
                            <i className="ri-save-line me-1"></i>
                            Save Changes
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          {modal_center && <div className="modal-backdrop fade show"></div>}
        </Container>
      </div>

      {/* Custom Quill Styles */}
      <style jsx>{`
        .ql-toolbar {
          border-top: 1px solid #ccc;
          border-left: 1px solid #ccc;
          border-right: 1px solid #ccc;
          border-bottom: none;
          border-radius: 0.375rem 0.375rem 0 0;
          background: #f8f9fa;
        }

        .ql-container {
          border-bottom: 1px solid #ccc;
          border-left: 1px solid #ccc;
          border-right: 1px solid #ccc;
          border-top: none;
          border-radius: 0 0 0.375rem 0.375rem;
          font-size: 14px;
        }

        .ql-editor {
          min-height: 300px;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
            "Helvetica Neue", Arial, sans-serif;
          line-height: 1.6;
        }

        .ql-editor h1 {
          font-size: 2rem;
          font-weight: bold;
          margin-bottom: 1rem;
          color: #212529;
        }

        .ql-editor h2 {
          font-size: 1.75rem;
          font-weight: bold;
          margin-bottom: 0.875rem;
          color: #212529;
        }

        .ql-editor h3 {
          font-size: 1.5rem;
          font-weight: 600;
          margin-bottom: 0.75rem;
          color: #212529;
        }

        .ql-editor h4 {
          font-size: 1.25rem;
          font-weight: 600;
          margin-bottom: 0.625rem;
          color: #212529;
        }

        .ql-editor p {
          margin-bottom: 1rem;
          color: #495057;
        }

        .ql-editor ul,
        .ql-editor ol {
          margin-bottom: 1rem;
          padding-left: 1.5rem;
        }

        .ql-editor li {
          margin-bottom: 0.5rem;
          color: #495057;
        }

        .ql-editor blockquote {
          border-left: 4px solid #007bff;
          padding-left: 1rem;
          margin: 1rem 0;
          font-style: italic;
          color: #6c757d;
        }

        .ql-editor .ql-code-block-container {
          background: #f8f9fa;
          border: 1px solid #e9ecef;
          border-radius: 0.25rem;
          padding: 1rem;
          margin: 1rem 0;
          font-family: Monaco, Consolas, "Lucida Console", monospace;
        }

        .spinner-border-sm {
          width: 1rem;
          height: 1rem;
        }
      `}</style>
    </React.Fragment>
  );
};

export default ContentPages;
